import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

import type { RouteLocationNormalized } from '@/services/routing/index.ts'
import type { Locale } from '@/services/translation/index.ts'
import { t } from '@/services/translation/index.ts'

const DEFAULT_TITLE = 'Anläggning'

const metaTitleWritable = ref(DEFAULT_TITLE)
const metaTitle = computed<string>(() => metaTitleWritable.value)
function setMetaTitle(title?: string | null): void {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  metaTitleWritable.value = title || DEFAULT_TITLE

  const { VITE_ENVIRONMENT_NAME } = import.meta.env
  const defaultTitle = `${t('general.app-name')} ${VITE_ENVIRONMENT_NAME === 'production' ? '' : ` (${VITE_ENVIRONMENT_NAME})`}`
  document.title = title ? title + ` | ${defaultTitle}` : defaultTitle
}
function useSetMetaTitle(title: Ref<string>): void {
  watch(title, () => setMetaTitle(title.value), { immediate: true })
}

const navbarTitleWritable = ref(DEFAULT_TITLE)
const navbarTitle = computed<string>(() => navbarTitleWritable.value)
function setNavbarTitle(title?: string | null): void {
  navbarTitleWritable.value = title ?? ''
}
function useSetNavbarTitle(title: Ref<string>): void {
  watch(title, () => setNavbarTitle(title.value), { immediate: true })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useAppTitles() {
  return { metaTitle, setMetaTitle, useSetMetaTitle, navbarTitle, setNavbarTitle, useSetNavbarTitle }
}

export function useInitAppTitles(route: RouteLocationNormalized, locale: Locale): void {
  const { setMetaTitle, setNavbarTitle } = useAppTitles()
  watch(
    [
      () => route.meta.title,
      () => route.meta.navTitle,
      // rerun when translation changes
      () => locale,
    ],
    ([currentTitleFn, currentNavTitleFn]) => {
      const headTitle = currentTitleFn?.(route)
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const navTitle = currentNavTitleFn?.(route) || headTitle
      if (headTitle) setMetaTitle(headTitle)
      if (navTitle) setNavbarTitle(navTitle)
    },
    { immediate: true }
  )
}
